import React, { useState, useEffect, useCallback } from 'react';
import { _T, C, CSS } from '../../shared/theme';
import { s_TI } from '../../shared/styles';
import Portal from '../../shared/Portal';
import { getServices, createService, updateService, deleteService, getBookings, updateBooking, createBooking, getCustomers } from './businessApi';

const CATEGORIES = [
  { id: 'wellness',   icon: '💚', nl: 'Welzijn',         en: 'Wellness' },
  { id: 'fitness',    icon: '💪', nl: 'Fitness',          en: 'Fitness' },
  { id: 'coaching',   icon: '🧠', nl: 'Coaching',         en: 'Coaching' },
  { id: 'nutrition',  icon: '🥗', nl: 'Voeding',          en: 'Nutrition' },
  { id: 'other',      icon: '⚡', nl: 'Overig',           en: 'Other' },
];
const CURRENCIES = ['EUR','USD','GBP','CHF'];

const catConfig = (id) => CATEGORIES.find(c => c.id === id) || CATEGORIES[CATEGORIES.length - 1];

const EMPTY_FORM = { name: '', description: '', duration_min: 60, price: '', currency: 'EUR', category: 'wellness', active: true };

const EMPTY_BOOK = { customer_id: '', customer_name: '', date: '', time: '10:00', notes: '' };

export default function BusinessServicesScreen({ lang, businessId, role, onOpenBookings }) {
  const nl       = lang === 'nl';
  const canEdit  = role === 'owner' || role === 'admin';
  const [tab, setTab]               = useState('services');
  const [services, setServices]     = useState([]);
  const [bookings, setBookings]     = useState([]);
  const [customers, setCustomers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [form, setForm]             = useState(EMPTY_FORM);
  const [editId, setEditId]         = useState(null);
  const [showForm, setShowForm]     = useState(false);
  const [saving, setSaving]         = useState(false);
  const [catFilter, setCatFilter]   = useState('all');
  const [bookingService, setBookingService] = useState(null); // service to book
  const [bookForm, setBookForm]     = useState(EMPTY_BOOK);
  const [bookSaving, setBookSaving] = useState(false);
  const [custSearch, setCustSearch] = useState('');

  const loadAll = useCallback(() => {
    setLoading(true);
    Promise.all([
      getServices(businessId),
      getBookings(businessId),
      getCustomers(businessId),
    ]).then(([svcs, bkgs, custs]) => {
      setServices(Array.isArray(svcs) ? svcs : []);
      setBookings(Array.isArray(bkgs) ? bkgs : []);
      setCustomers(Array.isArray(custs) ? custs : []);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [businessId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openNew = () => { setForm(EMPTY_FORM); setEditId(null); setShowForm(true); };
  const openEdit = (s) => {
    setForm({ name: s.name, description: s.description || '', duration_min: s.duration_min, price: s.price ?? '', currency: s.currency || 'EUR', category: s.category, active: !!s.active });
    setEditId(s.id);
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    const payload = { ...form, price: form.price !== '' ? parseFloat(form.price) : null, active: form.active ? 1 : 0, duration_min: parseInt(form.duration_min) };
    if (editId) await updateService(businessId, editId, payload).catch(() => {});
    else        await createService(businessId, payload).catch(() => {});
    setSaving(false);
    setShowForm(false);
    loadAll();
  };

  const handleDelete = async (id) => {
    if (!window.confirm(nl ? 'Service verwijderen?' : 'Delete this service?')) return;
    await deleteService(businessId, id).catch(() => {});
    loadAll();
  };

  const handleBookingStatus = async (bid, status) => {
    await updateBooking(businessId, bid, { status }).catch(() => {});
    setBookings(prev => prev.map(b => b.id === bid ? { ...b, status } : b));
  };

  const handleCreateBooking = async () => {
    if (!bookingService || !bookForm.date || !bookForm.time) return;
    setBookSaving(true);
    const cust = customers.find(c => c.id === bookForm.customer_id);
    await createBooking(businessId, {
      service_id: bookingService.id,
      service_name: bookingService.name,
      customer_id: bookForm.customer_id || null,
      booker_name: cust ? `${cust.first_name} ${cust.last_name}` : bookForm.customer_name,
      booker_email: cust?.email || '',
      scheduled_at: `${bookForm.date}T${bookForm.time}`,
      notes: bookForm.notes,
      status: 'confirmed',
      duration_min: bookingService.duration_min,
      price: bookingService.price,
      currency: bookingService.currency,
    }).catch(() => {});
    setBookSaving(false);
    setBookingService(null);
    setBookForm(EMPTY_BOOK);
    setCustSearch('');
    loadAll();
  };

  const filtered = catFilter === 'all' ? services : services.filter(s => s.category === catFilter);
  const pendingBookings = bookings.filter(b => b.status === 'pending');

  if (loading) return <div style={{ padding: 24, textAlign: 'center', color: C.silver, fontSize: 13 }}>...</div>;

  return (
    <div style={{ padding: '0 0 32px' }}>
      <style>{CSS}</style>

      {/* ── Services ── */}
      {true && (
        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
            <div style={{ color: C.white, fontSize: 15, fontWeight: 700 }}>{nl ? 'Diensten' : 'Services'}</div>
            {canEdit && (
              <button onClick={openNew}
                style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}55`, borderRadius: 10, color: _T.accent, fontSize: 13, fontWeight: 600, padding: '8px 14px', cursor: 'pointer' }}>
                + {nl ? 'Toevoegen' : 'Add'}
              </button>
            )}
          </div>

          {/* Category filter */}
          <div style={{ display: 'flex', gap: 6, overflowX: 'auto', paddingBottom: 4, marginBottom: 14 }}>
            <button onClick={() => setCatFilter('all')}
              style={{ flexShrink: 0, background: catFilter === 'all' ? `${_T.accent}20` : _T.bg4, border: `1.5px solid ${catFilter === 'all' ? _T.accent : _T.cardBorderBot}`, borderRadius: 20, color: catFilter === 'all' ? _T.accent : C.silver, fontSize: 11, fontWeight: 600, padding: '5px 12px', cursor: 'pointer' }}>
              {nl ? 'Alles' : 'All'}
            </button>
            {CATEGORIES.map(c => (
              <button key={c.id} onClick={() => setCatFilter(f => f === c.id ? 'all' : c.id)}
                style={{ flexShrink: 0, background: catFilter === c.id ? `${_T.accent}20` : _T.bg4, border: `1.5px solid ${catFilter === c.id ? _T.accent : _T.cardBorderBot}`, borderRadius: 20, color: catFilter === c.id ? _T.accent : C.silver, fontSize: 11, fontWeight: 600, padding: '5px 12px', cursor: 'pointer' }}>
                {c.icon} {nl ? c.nl : c.en}
              </button>
            ))}
          </div>

          {/* Service form */}
          {showForm && canEdit && (
            <div style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 16, border: `1px solid ${_T.accent}44` }}>
              <div style={{ color: C.white, fontSize: 14, fontWeight: 600, marginBottom: 12 }}>
                {editId ? (nl ? 'Service bewerken' : 'Edit Service') : (nl ? 'Nieuwe service' : 'New Service')}
              </div>
              <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                placeholder={nl ? 'Naam *' : 'Name *'}
                style={{ ...s_TI({ width: '100%', boxSizing: 'border-box', marginBottom: 8 }), fontSize: 13 }} />
              <textarea value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                placeholder={nl ? 'Omschrijving' : 'Description'}
                rows={3}
                style={{ ...s_TI({ width: '100%', boxSizing: 'border-box', marginBottom: 8 }), fontSize: 13, resize: 'vertical' }} />
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
                <label style={{ color: C.silver, fontSize: 11 }}>
                  {nl ? 'Duur (min)' : 'Duration (min)'}
                  <input type="number" value={form.duration_min} onChange={e => setForm(f => ({ ...f, duration_min: e.target.value }))}
                    style={{ ...s_TI({ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 4 }), fontSize: 13 }} />
                </label>
                <label style={{ color: C.silver, fontSize: 11 }}>
                  {nl ? 'Prijs' : 'Price'}
                  <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
                    <select value={form.currency} onChange={e => setForm(f => ({ ...f, currency: e.target.value }))}
                      style={{ ...s_TI({ width: 56, flexShrink: 0 }), fontSize: 11, padding: '8px 4px' }}>
                      {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input type="number" value={form.price} onChange={e => setForm(f => ({ ...f, price: e.target.value }))}
                      placeholder={nl ? 'Optioneel' : 'Optional'}
                      style={{ ...s_TI({ flex: 1, boxSizing: 'border-box' }), fontSize: 13 }} />
                  </div>
                </label>
              </div>
              {/* Category */}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
                {CATEGORIES.map(c => (
                  <button key={c.id} onClick={() => setForm(f => ({ ...f, category: c.id }))}
                    style={{ padding: '5px 10px', borderRadius: 20, border: `1.5px solid ${form.category === c.id ? _T.accent : _T.cardBorderBot}`, background: form.category === c.id ? `${_T.accent}20` : _T.bg4, color: form.category === c.id ? _T.accent : C.silver, fontSize: 11, fontWeight: form.category === c.id ? 700 : 400, cursor: 'pointer' }}>
                    {c.icon} {nl ? c.nl : c.en}
                  </button>
                ))}
              </div>
              {/* Active toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
                <button onClick={() => setForm(f => ({ ...f, active: !f.active }))}
                  style={{ width: 40, height: 22, borderRadius: 11, background: form.active ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: `1px solid ${form.active ? _T.accent : _T.cardBorderBot}`, cursor: 'pointer', position: 'relative', padding: 0, transition: 'all 0.2s' }}>
                  <div style={{ position: 'absolute', top: 3, left: form.active ? 20 : 3, width: 16, height: 16, borderRadius: '50%', background: form.active ? '#0a1220' : C.silver, transition: 'left 0.2s' }} />
                </button>
                <span style={{ color: C.silver, fontSize: 12 }}>{nl ? 'Actief (zichtbaar op publieke pagina)' : 'Active (visible on public page)'}</span>
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <button onClick={() => setShowForm(false)}
                  style={{ flex: 1, background: 'transparent', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.silver, fontSize: 13, padding: '10px', cursor: 'pointer' }}>
                  {nl ? 'Annuleren' : 'Cancel'}
                </button>
                <button onClick={handleSave} disabled={saving || !form.name.trim()}
                  style={{ flex: 2, background: `linear-gradient(135deg,${_T.grad1},${_T.grad2})`, border: 'none', borderRadius: 10, color: '#0a1220', fontSize: 13, fontWeight: 700, padding: '10px', cursor: 'pointer', opacity: form.name.trim() ? 1 : 0.5 }}>
                  {saving ? '...' : (editId ? (nl ? 'Opslaan' : 'Save') : (nl ? 'Toevoegen' : 'Add Service'))}
                </button>
              </div>
            </div>
          )}

          {filtered.length === 0 ? (
            <div style={{ textAlign: 'center', color: C.silver, padding: 48 }}>
              <div style={{ fontSize: 40, marginBottom: 8 }}>🩺</div>
              <div style={{ fontSize: 15 }}>{nl ? 'Nog geen services' : 'No services yet'}</div>
              {canEdit && <div style={{ fontSize: 12, color: C.silver2, marginTop: 4 }}>{nl ? 'Voeg je eerste dienst toe' : 'Add your first service'}</div>}
            </div>
          ) : (
            filtered.map(svc => {
              const cc = catConfig(svc.category);
              return (
                <div key={svc.id} style={{ background: _T.bg3, borderRadius: 16, padding: 16, marginBottom: 10, border: `1px solid ${_T.cardBorderBot}`, opacity: svc.active ? 1 : 0.55 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                        <span style={{ fontSize: 18 }}>{cc.icon}</span>
                        <span style={{ color: C.white, fontSize: 15, fontWeight: 700 }}>{svc.name}</span>
                        {!svc.active && <span style={{ fontSize: 9, color: C.silver2, background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 6, padding: '2px 6px' }}>{nl ? 'INACTIEF' : 'INACTIVE'}</span>}
                      </div>
                      <div style={{ display: 'flex', gap: 10, fontSize: 12, color: C.silver, marginBottom: svc.description ? 6 : 0 }}>
                        <span>⏱ {svc.duration_min}{nl ? ' min' : ' min'}</span>
                        {svc.price != null && <span>💶 {svc.currency} {parseFloat(svc.price).toFixed(2)}</span>}
                        <span style={{ color: _T.accent, fontSize: 10, fontWeight: 600, background: `${_T.accent}15`, border: `1px solid ${_T.accent}33`, borderRadius: 10, padding: '1px 7px' }}>{nl ? cc.nl : cc.en}</span>
                      </div>
                      {svc.description && <div style={{ color: C.silver2, fontSize: 12, lineHeight: 1.5 }}>{svc.description}</div>}
                    </div>
                    <div style={{ display: 'flex', gap: 6, marginLeft: 8, flexShrink: 0 }}>
                      <button onClick={() => { setBookingService(svc); setBookForm(EMPTY_BOOK); setCustSearch(''); }}
                        style={{ background: `${_T.accent}20`, border: `1px solid ${_T.accent}44`, borderRadius: 8, color: _T.accent, fontSize: 11, fontWeight: 700, padding: '6px 10px', cursor: 'pointer' }}>
                        📅 {nl ? 'Boeken' : 'Book'}
                      </button>
                      {canEdit && <>
                        <button onClick={() => openEdit(svc)} style={{ background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 8, color: C.silver, fontSize: 12, padding: '6px 10px', cursor: 'pointer' }}>✏️</button>
                        <button onClick={() => handleDelete(svc.id)} style={{ background: `#C0505015`, border: `1px solid #C0505044`, borderRadius: 8, color: '#C05050', fontSize: 12, padding: '6px 10px', cursor: 'pointer' }}>🗑</button>
                      </>}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </>
      )}

      {/* ── Quick Booking Modal ── */}
      {bookingService && (
        <Portal>
        <div style={{ position: 'fixed', inset: 0, zIndex: 500, background: 'rgba(5,8,18,0.88)', backdropFilter: 'blur(8px)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center' }}
          onClick={e => { if (e.target === e.currentTarget) setBookingService(null); }}>
          <div style={{ width: '100%', maxWidth: 480, background: _T.bg2, borderRadius: '20px 20px 0 0', border: `1px solid ${_T.cardBorderTop}`, padding: '20px 20px 40px', maxHeight: '85vh', overflowY: 'auto' }}>
            <div style={{ width: 32, height: 4, borderRadius: 2, background: _T.cardBorderBot, margin: '0 auto 16px' }} />
            <div style={{ color: C.white, fontSize: 16, fontWeight: 700, marginBottom: 4 }}>
              📅 {nl ? 'Afspraak plannen' : 'Schedule Appointment'}
            </div>
            <div style={{ color: _T.accent, fontSize: 13, marginBottom: 16 }}>
              {bookingService.name} · {bookingService.duration_min}min{bookingService.price ? ` · ${bookingService.currency} ${parseFloat(bookingService.price).toFixed(2)}` : ''}
            </div>

            {/* Customer picker */}
            <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 8 }}>{nl ? 'KLANT' : 'CUSTOMER'}</div>
            <input value={custSearch} onChange={e => { setCustSearch(e.target.value); setBookForm(f => ({ ...f, customer_id: '', customer_name: e.target.value })); }}
              placeholder={nl ? 'Zoek klant op naam of email...' : 'Search customer by name or email...'}
              style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 6, outline: 'none', boxSizing: 'border-box' }} />
            {custSearch.length > 0 && customers.filter(c => `${c.first_name} ${c.last_name} ${c.email}`.toLowerCase().includes(custSearch.toLowerCase())).slice(0, 5).map(c => (
              <div key={c.id} onClick={() => { setBookForm(f => ({ ...f, customer_id: c.id, customer_name: `${c.first_name} ${c.last_name}` })); setCustSearch(`${c.first_name} ${c.last_name}`); }}
                style={{ background: bookForm.customer_id === c.id ? `${_T.accent}20` : _T.bg3, border: `1px solid ${bookForm.customer_id === c.id ? _T.accent + '55' : _T.cardBorderBot}`, borderRadius: 10, padding: '8px 12px', marginBottom: 4, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{ width: 30, height: 30, borderRadius: '50%', background: `${_T.accent}20`, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, color: _T.accent, fontWeight: 700 }}>
                  {(c.first_name || '?')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ color: C.white, fontSize: 13, fontWeight: 600 }}>{c.first_name} {c.last_name}</div>
                  <div style={{ color: C.silver2, fontSize: 11 }}>{c.email}</div>
                </div>
              </div>
            ))}

            {/* Date & time */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 12, marginBottom: 8 }}>
              <div>
                <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 6 }}>{nl ? 'DATUM' : 'DATE'}</div>
                <input type="date" value={bookForm.date} onChange={e => setBookForm(f => ({ ...f, date: e.target.value }))}
                  style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div>
                <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 6 }}>{nl ? 'TIJD' : 'TIME'}</div>
                <input type="time" value={bookForm.time} onChange={e => setBookForm(f => ({ ...f, time: e.target.value }))}
                  style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', outline: 'none', boxSizing: 'border-box' }} />
              </div>
            </div>

            {/* Notes */}
            <div style={{ fontSize: 10, color: C.silver, letterSpacing: 2, marginBottom: 6, marginTop: 4 }}>{nl ? 'NOTITIES' : 'NOTES'}</div>
            <textarea value={bookForm.notes} onChange={e => setBookForm(f => ({ ...f, notes: e.target.value }))}
              placeholder={nl ? 'Optionele notities...' : 'Optional notes...'}
              rows={2}
              style={{ width: '100%', background: _T.bg4, border: `1px solid ${_T.cardBorderBot}`, borderRadius: 10, color: C.white, fontSize: 13, padding: '10px 12px', marginBottom: 16, outline: 'none', boxSizing: 'border-box', resize: 'none', fontFamily: 'inherit' }} />

            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => setBookingService(null)} style={{ flex: 1, background: 'none', border: `1px solid ${_T.cardBorderBot}`, borderRadius: 12, color: C.silver, fontSize: 13, padding: '12px', cursor: 'pointer' }}>
                {nl ? 'Annuleren' : 'Cancel'}
              </button>
              <button onClick={handleCreateBooking} disabled={bookSaving || !bookForm.date || !bookForm.time}
                style={{ flex: 2, background: bookForm.date && bookForm.time ? `linear-gradient(135deg,${_T.grad1},${_T.grad2})` : _T.bg4, border: 'none', borderRadius: 12, color: bookForm.date && bookForm.time ? '#0a1220' : C.silver2, fontWeight: 700, fontSize: 13, padding: '12px', cursor: bookForm.date && bookForm.time ? 'pointer' : 'not-allowed' }}>
                {bookSaving ? '...' : (nl ? '✓ Afspraak plannen' : '✓ Schedule Appointment')}
              </button>
            </div>

            {(!bookForm.customer_id && !bookForm.customer_name) && (
              <div style={{ marginTop: 10, fontSize: 11, color: '#C8A84B', textAlign: 'center' }}>
                ⚠ {nl ? 'Klant selecteren of naam invullen hierboven' : 'Select a customer or type a name above'}
              </div>
            )}
          </div>
        </div>
        </Portal>
      )}

    </div>
  );
}
